import { useState, useRef } from 'react';
import { MoreVertical } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

export interface DropdownAction {
  id: string;
  label: string;
  icon: 'cancel' | 'extend' | 'shrink' | 'repeat' | 'delete';
  variant?: 'default' | 'destructive';
  onClick: () => void;
}

interface ActionDropdownProps {
  actions: DropdownAction[];
  className?: string;
  onOpenChange?: (open: boolean) => void;
}

const iconMap = {
  cancel: '/assets/icons/x.svg',
  extend: '/assets/icons/expand.svg',
  shrink: '/assets/icons/shrink.svg',
  repeat: '/assets/icons/repeat.svg',
  delete: '/assets/icons/delete.svg',
};

export default function ActionDropdown({
  actions,
  className = '',
  onOpenChange,
}: ActionDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    onOpenChange?.(open);

    // Remove focus from trigger when closing dropdown
    if (!open && triggerRef.current) {
      triggerRef.current.blur();
    }
  };

  return (
    <DropdownMenu open={isOpen} onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>
        <Button
          ref={triggerRef}
          variant="ghost"
          size="sm"
          className={`flex h-[2rem] w-[2rem] items-center justify-center rounded-full border border-[#e4e4e4] bg-[#ebebeb] p-0 hover:bg-[#e0e0e0] focus:ring-0 focus:outline-none ${className}`}
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <MoreVertical className="h-[1.25rem] w-[1.25rem] text-black" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-[17rem] rounded-2xl border-none bg-white p-2 shadow-[0px_8px_24px_-8px_rgba(31,31,31,0.08)]"
      >
        {actions.map((action) => {
          const iconSrc = iconMap[action.icon];

          return (
            <DropdownMenuItem
              key={action.id}
              onClick={(e) => {
                e.stopPropagation();
                action.onClick();
              }}
              className={`flex h-[3.5rem] w-full cursor-pointer items-center gap-3 rounded-none px-2 py-3 hover:bg-transparent focus:bg-transparent data-[highlighted]:bg-transparent ${
                action.variant === 'destructive'
                  ? 'text-[#e5484d] hover:!rounded-xl hover:!bg-[#f9f9f9]'
                  : 'text-black hover:!rounded-xl hover:!bg-gray-50'
              } `}
            >
              <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center">
                <img src={iconSrc} alt={action.label} className="h-6 w-6" />
              </div>
              <span className="font-['Helvetica_Neue_LT_GEO:55_Roman',_sans-serif] text-[15px] leading-[24px] font-normal">
                {action.label}
              </span>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
